// app/javascript/controllers/ui/manifesto/counter_up_controller.js

import { Controller } from "@hotwired/stimulus"

/**
 * Counter Up Controller — Split-Flap
 *
 * Каждая цифра — колонка высотой 1em с вертикальным стеком цифр.
 * Стек содержит ROUNDS повторов 0-9, чтобы можно было прокрутить N раз
 * и в итоге приземлиться на нужную цифру (визуальный "spin" эффект).
 *
 * @usedby _counter_strip.html.erb
 *
 * Usage:
 *   <span data-controller="ui--manifesto--counter-up"
 *         data-ui--manifesto--counter-up-to-value="21"
 *         data-ui--manifesto--counter-up-suffix-value="+">21</span>
 */
export default class extends Controller {
  static values = {
    from: { type: Number, default: 0 },
    to: { type: Number, default: 0 },
    suffix: { type: String, default: "" },
    duration: { type: Number, default: 1600 },
    threshold: { type: Number, default: 0.3 }
  }

  // Количество повторов 0-9 в стеке. Позволяет "прокрутить" через несколько
  // полных оборотов перед приземлением на цель.
  static ROUNDS = 4

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.element.textContent = `${this.toValue}${this.suffixValue}`
      return
    }

    this.#build()
    this.#observe()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  #build() {
    const target = String(Math.max(this.toValue, this.fromValue))
    this.element.innerHTML = ""
    this.element.classList.add("mf-flap")
    this.columns = []

    for (const ch of target) {
      if (/\d/.test(ch)) {
        const col = document.createElement("span")
        col.className = "mf-flap__col"
        const stack = document.createElement("span")
        stack.className = "mf-flap__stack"

        // ROUNDS повторов 0-9 в стеке (40 элементов при ROUNDS=4)
        for (let r = 0; r < this.constructor.ROUNDS; r++) {
          for (let i = 0; i <= 9; i++) {
            const s = document.createElement("span")
            s.textContent = String(i)
            stack.appendChild(s)
          }
        }
        col.appendChild(stack)
        this.element.appendChild(col)
        // Стартуем с середины стека — чтобы можно было и вверх и вниз крутить
        // Но проще стартовать с 0, анимировать до (последний виток * 10 + digit).
        stack.style.transform = "translateY(0)"
        this.columns.push({ stack, digit: Number(ch) })
      } else {
        const lit = document.createElement("span")
        lit.className = "mf-flap__lit"
        lit.textContent = ch
        this.element.appendChild(lit)
      }
    }

    if (this.suffixValue) {
      const sfx = document.createElement("span")
      sfx.className = "mf-flap__suffix"
      sfx.textContent = this.suffixValue
      this.element.appendChild(sfx)
    }
  }

  #observe() {
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#play()
          this.observer.disconnect()
          break
        }
      }
    }, { threshold: this.thresholdValue })
    this.observer.observe(this.element)
  }

  #play() {
    const ROUNDS = this.constructor.ROUNDS
    const totalDigits = ROUNDS * 10   // e.g. 40

    this.columns.forEach(({ stack, digit }, i) => {
      const delay = i * 80
      // Приземление на (ROUNDS - 1) * 10 + digit  — последний "виток" стека
      const landAtIndex = (ROUNDS - 1) * 10 + digit
      // Переводим в % от высоты стека (totalDigits * 1em)
      // Сдвиг = landAtIndex * (100 / totalDigits)%
      const translatePercent = (landAtIndex * 100) / totalDigits
      setTimeout(() => {
        stack.style.transition = `transform ${this.durationValue}ms var(--mf-ease)`
        stack.style.transform = `translateY(-${translatePercent}%)`
      }, delay)
    })
  }
}
